import { ChevronLeft, ChevronRight, History, RefreshCw, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { api } from "../../api/client.js";
import type { AuditEvent } from "../../api/types.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { Badge } from "../../components/Badge.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import { formatDateTime, titleCase } from "../../lib/format.js";
import {
  ACTION_PRESETS,
  ENTITY_TYPES,
  auditActionLabel,
  auditActionTone,
  auditDetailEntries,
  auditEntityHref,
  auditHeadline,
} from "./audit-projection.js";
import "./audit-queue.css";

export function AuditPage() {
  const [query, setQuery] = useState("");
  const [entityType, setEntityType] = useState<AuditEvent["entityType"] | "">("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const pageSize = 50;
  const debouncedQuery = useDebouncedValue(query, 250);
  const debouncedAction = useDebouncedValue(action, 250);
  const loadAudit = useCallback(
    async (signal: AbortSignal) => {
      const trimmedQuery = debouncedQuery.trim();
      if (trimmedQuery) {
        const exact = await api.getAuditEvents({ page, pageSize, entityId: trimmedQuery }, signal);
        if (exact.items.length > 0) return exact;
      }
      return api.getAuditEvents(
        {
          page,
          pageSize,
          ...(trimmedQuery ? { query: trimmedQuery } : {}),
          ...(entityType ? { entityType } : {}),
          ...(debouncedAction.trim() ? { action: debouncedAction.trim() } : {}),
        },
        signal,
      );
    },
    [debouncedAction, debouncedQuery, entityType, page],
  );
  const audit = useApiResource(loadAudit);
  const items = audit.data?.items ?? [];
  const actions = useMemo(() => [...new Set(items.map((event) => event.action))].sort(), [items]);
  const hasNext = audit.data?.total === -1 || items.length === pageSize;
  const findingLinked = items.filter((event) => event.entityType === "finding").length;
  const actorCount = new Set(items.map((event) => event.actorId)).size;

  return (
    <section className="page audit-page" aria-labelledby="audit-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Append-only ledger</span>
          <h1 id="audit-heading">Audit trail</h1>
          <p>Operator actions and durable review-state changes, newest first.</p>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Refresh audit trail"
          title="Refresh audit trail"
          disabled={audit.isRefreshing}
          onClick={audit.refresh}
        >
          <RefreshCw
            aria-hidden="true"
            className={audit.isRefreshing ? "spin" : undefined}
            size={17}
          />
        </button>
      </div>

      <section className="evidence-decision-hero" aria-label="Audit page summary">
        <div>
          <span className="page-kicker">This page</span>
          <h2>What changed, who did it, and which record it touched.</h2>
          <p>{auditHeadline({ eventCount: items.length, findingLinked, actorCount })}</p>
        </div>
        <dl className="evidence-hero-metrics">
          <div data-metric="events">
            <dt>Events</dt>
            <dd>{items.length}</dd>
          </div>
          <div data-metric="findings">
            <dt>Finding-linked</dt>
            <dd>{findingLinked}</dd>
          </div>
          <div data-metric="actors">
            <dt>Actors</dt>
            <dd>{actorCount}</dd>
          </div>
        </dl>
      </section>

      <div className="list-toolbar list-toolbar--audit">
        <label className="search-control">
          <Search aria-hidden="true" size={16} />
          <span className="sr-only">Search audit trail</span>
          <input
            type="search"
            value={query}
            placeholder="Search actor, action, or paste an entity ID"
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="select-control">
          <span>Entity</span>
          <select
            value={entityType}
            onChange={(event) => {
              setEntityType(event.target.value as AuditEvent["entityType"] | "");
              setPage(1);
            }}
          >
            <option value="">All entities</option>
            {ENTITY_TYPES.map((value) => (
              <option value={value} key={value}>
                {titleCase(value.replaceAll("_", " "))}
              </option>
            ))}
          </select>
        </label>
        <label className="select-control">
          <span>Action</span>
          <input
            list="audit-actions"
            value={action}
            placeholder="Any action"
            onChange={(event) => {
              setAction(event.target.value);
              setPage(1);
            }}
          />
          <datalist id="audit-actions">
            {[...new Set([...ACTION_PRESETS, ...actions])].map((value) => (
              <option value={value} key={value} />
            ))}
          </datalist>
        </label>
        {query || entityType || action ? (
          <button
            className="text-button"
            type="button"
            onClick={() => {
              setQuery("");
              setEntityType("");
              setAction("");
              setPage(1);
            }}
          >
            Clear all
          </button>
        ) : null}
      </div>

      <fieldset className="audit-action-chips">
        <legend className="sr-only">Common actions</legend>
        {ACTION_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className={action === preset ? "is-on" : ""}
            aria-label={`Filter to ${preset}`}
            aria-pressed={action === preset}
            onClick={() => {
              setAction(action === preset ? "" : preset);
              setPage(1);
            }}
          >
            {auditActionLabel(preset)}
          </button>
        ))}
      </fieldset>

      {audit.error && audit.data ? (
        <InlineError error={audit.error} onRetry={audit.refresh} />
      ) : null}
      {audit.isLoading && !audit.data ? <LoadingTable columns={6} rows={10} /> : null}
      {audit.error && !audit.data ? (
        <InlineError error={audit.error} onRetry={audit.refresh} />
      ) : null}
      {!audit.isLoading && audit.data && items.length === 0 ? (
        <EmptyState
          title="No audit events found"
          description="No recorded event matches the current actor, entity, or action filters."
        />
      ) : null}

      {items.length > 0 ? (
        <section className="audit-queue" aria-label="Audit events">
          {items.map((event) => {
            const href = auditEntityHref(event);
            const tone = auditActionTone(event.action);
            const open = openId === event.id;
            const details = auditDetailEntries(event.details);
            return (
              <div key={event.id}>
                <article className={`audit-row${open ? " is-open" : ""}`} data-tone={tone}>
                  <button
                    type="button"
                    className="audit-row__toggle"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : event.id)}
                  >
                    <time dateTime={event.createdAt}>{formatDateTime(event.createdAt)}</time>
                    <span>
                      <strong>{auditActionLabel(event.action)}</strong>
                      <small>{event.action}</small>
                    </span>
                    <span>
                      <small>Actor</small>
                      <code>{event.actorId}</code>
                    </span>
                  </button>
                  <span className="audit-row__entity">
                    <Badge
                      label={titleCase(event.entityType.replaceAll("_", " "))}
                      tone="neutral"
                    />
                    {href ? <a href={href}>{event.entityId}</a> : <code>{event.entityId}</code>}
                  </span>
                </article>
                {open ? (
                  <div className="audit-briefing">
                    {details.length === 0 ? (
                      <p className="muted-copy">No additional fields were recorded.</p>
                    ) : (
                      <dl>
                        {details.map((entry) => (
                          <div key={entry.key}>
                            <dt>{titleCase(entry.key)}</dt>
                            <dd>
                              {entry.href ? <a href={entry.href}>{entry.value}</a> : entry.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>
      ) : null}

      {page > 1 || hasNext ? (
        <nav className="pagination" aria-label="Audit pages">
          <span>Page {page}</span>
          <button
            className="icon-button"
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            <ChevronLeft aria-hidden="true" size={17} />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="Next page"
            disabled={!hasNext}
            onClick={() => setPage((current) => current + 1)}
          >
            <ChevronRight aria-hidden="true" size={17} />
          </button>
        </nav>
      ) : null}

      <p className="summary-line">
        <History aria-hidden="true" size={15} />
        Newest events first · immutable actor and entity references
      </p>
    </section>
  );
}
